import React from 'react';
import { Link } from 'react-router-dom';
import { Brain, CheckCircle, ArrowRight, ArrowLeft, Clock, Users, Target } from 'lucide-react';

const EmotionalIntelligencePage = () => {
  const topics = [
    'Kas yra emocinis intelektas? Kodėl jis svarbus ir kuo gali būti naudingas?',
    'Kas yra emocijos? Kokia jų funkcija mūsų gyvenime? Kokie mitai, nuostatos ir įsitikinimai apie emocijas mums kenkia?',
    'Savęs pažinimas: Kaip suprasti, atpažinti ir priimti savo emocijas?',
    'Emocijų dedamosios dalys: Kūno pojūčiai, mintys, emocijų intensyvumas ir jų įtaka',
    'Kito žmogaus emocijų supratimas: Empatija, aktyvus klausymasis, kitų žmonių emocijų atpažinimas ir priėmimas',
    'Emocijų reguliavimas: Kaip reguliuoti stiprias emocijas, tokias kaip nerimas, baimė ar pyktis? Pateikiamos praktinės technikos',
    'Santykių reguliavimas: Kaip bendrauti sudėtingose situacijose, valdyti konfliktus ir kurti sveikus santykius net su sudėtingomis asmenybėmis?'
  ];

  const benefits = [
    'Išmoksite geriau suprasti savo emocijas ir jų įtaką sprendimams bei veiksmams',
    'Ugdysite gebėjimą kurti tikrus ryšius su kolegomis ir kitais žmonėmis',
    'Išmoksite, kaip efektyviau reaguoti į stresines situacijas ir palaikyti emocinę pusiausvyrą',
    'Pagerinsite bendravimo įgūdžius ir komandinį darbą',
    'Sustiprinsite pasitikėjimą savimi sprendžiant konfliktus ir mažinant nesusikalbėjimą'
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="bg-[#4CAF50] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <Link 
            to="/mokymai" 
            className="inline-flex items-center text-white/90 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Grįžti į visus mokymus
          </Link>
          
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Brain className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-['Poppins'] font-bold text-white">
              Emocinio intelekto mokymai
            </h1>
          </div>
          <p className="text-xl text-white/90 leading-relaxed">
            5 susitikimai po 4 val. | Gilaus pokyčio programa
          </p>
        </div>
      </section>

      {/* Quick Info */}
      <section className="py-8 bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="flex flex-wrap gap-6 justify-center">
            <div className="flex items-center space-x-2 text-[#718096]">
              <Clock className="w-5 h-5 text-[#4CAF50]" />
              <span>5 susitikimai po 4 val.</span>
            </div>
            <div className="flex items-center space-x-2 text-[#718096]">
              <Users className="w-5 h-5 text-[#FF9F43]" />
              <span>Komandoms ir asmenims</span>
            </div>
            <div className="flex items-center space-x-2 text-[#718096]">
              <Target className="w-5 h-5 text-[#5B9BD5]" />
              <span>Gilus pokytis</span>
            </div>
          </div>
        </div>
      </section>

      {/* Did You Know */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="bg-[#E8F5E9] rounded-3xl p-8 border-l-4 border-[#4CAF50]">
            <h3 className="text-2xl font-['Poppins'] font-semibold mb-4 text-[#2D3748]">
              Ar žinojote?
            </h3>
            <p className="text-lg text-[#718096] leading-relaxed">
              Tyrimai rodo, kad emocinis intelektas (EI) lemia daugiau nei 58% darbo ir net asmeninės sėkmės bei produktyvumo. Žmonės, kurie supranta savo ir kitų emocijas, lengviau randa bendrą kalbą, geriau sprendžia konfliktus ir pasiekia aukštesnių rezultatų. Emocinis intelektas – tai tiltas, jungiantis asmeninę savivoką ir komandinį efektyvumą, todėl jo ugdymas yra ne tik naudingas, bet ir būtinas.
            </p>
          </div>
        </div>
      </section>

      {/* Training Goal */}
      <section className="py-12 bg-[#FAFAF8]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="bg-white rounded-3xl p-8 border border-slate-100">
            <h2 className="text-2xl font-['Poppins'] font-semibold mb-4 text-[#2D3748]">
              Mokymų tikslas
            </h2>
            <p className="text-[#718096] leading-relaxed">
              Padėti jums ir jūsų komandai geriau pažinti save ir vienas kitą bei stiprinti tarpusavio ryšį. Šie mokymai skirti tiems, kurie nori gilaus pokyčio t.y. ne tik pagerinti bendravimą, bet ir gilintis į savo emocijas, augti ir keisti nusistovėjusias nuostatas.
            </p>
          </div>
        </div>
      </section>

      {/* Topics */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <h2 className="text-3xl font-['Poppins'] font-bold text-[#2D3748] mb-8 text-center">
            Mokymų potemės
          </h2>
          <div className="space-y-4">
            {topics.map((topic, index) => (
              <div
                key={index}
                className="flex items-start space-x-4 bg-[#FAFAF8] p-5 rounded-2xl hover:bg-[#E8F5E9] transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-[#4CAF50] text-white flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                  {index + 1}
                </div>
                <p className="text-[#718096] leading-relaxed">{topic}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-[#FAFAF8]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <h2 className="text-3xl font-['Poppins'] font-bold text-[#2D3748] mb-8 text-center">
            Ko išmoksite?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-start space-x-3 bg-white p-5 rounded-2xl border border-slate-100"
              >
                <CheckCircle className="w-6 h-6 text-[#4CAF50] flex-shrink-0 mt-0.5" />
                <p className="text-[#718096]">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#4CAF50] py-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="text-3xl font-['Poppins'] font-bold text-white mb-4">
            Norite užsisakyti šiuos mokymus?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Susisiekite ir aptarkime, kaip galiu padėti jūsų komandai
          </p>
          <Link
            to="/kontaktai"
            className="inline-flex items-center bg-white text-[#4CAF50] hover:bg-slate-100 rounded-full px-8 py-4 font-semibold transition-colors"
          >
            Gauti pasiūlymą
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default EmotionalIntelligencePage;
